import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  LoadEffectiveTimeBlocks,
  ResolveTimeBlockLabel,
  TimeBlock,
  TimeBlockLookup,
} from '@/shared/domain'
import { defaultTimeBlock, findTimeBlock, timeBlockScopeKey } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { formatTimeBlockLabel } from '@/shared/ui/components/time-blocks/format-time-block-label'
import type { EffectiveTimeBlocksOrgContext } from './use-effective-time-blocks'

function uniqueScopes(scopes: ReadonlyArray<TimeBlockLookup | null>): Map<string, TimeBlockLookup | null> {
  const byKey = new Map<string, TimeBlockLookup | null>()
  for (const scope of scopes) byKey.set(timeBlockScopeKey(scope), scope)
  return byKey
}

export function useTimeBlockLabels(
  loadEffectiveTimeBlocks: LoadEffectiveTimeBlocks,
  orgContext: EffectiveTimeBlocksOrgContext | null,
  scopes: ReadonlyArray<TimeBlockLookup | null>,
): ResolveTimeBlockLabel {
  const [blocksByScope, setBlocksByScope] = useState<ReadonlyMap<string, TimeBlock[]>>(
    () => new Map(),
  )
  const loadRef = useRef(loadEffectiveTimeBlocks)
  loadRef.current = loadEffectiveTimeBlocks
  const scopesRef = useRef(scopes)
  scopesRef.current = scopes
  const requestedRef = useRef<Set<string>>(new Set())

  const orgId = orgContext?.orgId ?? null
  const orgType = orgContext?.orgType ?? null
  const orgKey = orgId && orgType ? `${orgType}:${orgId}` : null
  const orgKeyRef = useRef(orgKey)
  orgKeyRef.current = orgKey
  const scopesKey = [...uniqueScopes(scopes).keys()].sort().join('|')

  useEffect(() => {
    requestedRef.current = new Set()
    setBlocksByScope(new Map())
  }, [orgKey])

  useEffect(() => {
    if (!orgId || !orgType || !orgKey) return
    for (const [key, scope] of uniqueScopes(scopesRef.current)) {
      if (requestedRef.current.has(key)) continue
      requestedRef.current.add(key)
      loadRef
        .current({ orgId, orgType, ...(scope ?? {}) })
        .then((result) => {
          if (orgKeyRef.current !== orgKey) return
          setBlocksByScope((previous) => new Map(previous).set(key, result.timeBlocks))
        })
        .catch((error: unknown) => {
          reportError(error, { fallbackMessage: 'Failed to load time blocks.' })
        })
    }
  }, [orgId, orgType, orgKey, scopesKey])

  return useCallback<ResolveTimeBlockLabel>(
    (query) => {
      const blocks = blocksByScope.get(timeBlockScopeKey(query.scope))
      if (!blocks) return null
      const block =
        findTimeBlock(blocks, query.huntType, query.timePeriod) ?? defaultTimeBlock(query.timePeriod)
      return block ? formatTimeBlockLabel(block) : null
    },
    [blocksByScope],
  )
}
